import { ClickAwayListener } from "@mui/material";
import { CHATBOT_USER_ID } from "@redotech/ai-autopilot-constants/constant";
import { useRequiredContext } from "@redotech/react-util/context";
import { useSearch } from "@redotech/react-util/search";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { GetUser as User } from "@redotech/redo-model/user";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  RedoList,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Divider } from "@redotech/redo-web/divider";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import ChatBotIcon from "@redotech/redo-web/icon-old/chat-bot.svg";
import { UserImageSize } from "@redotech/redo-web/user-image";
import Fuse from "fuse.js";
import { memo, useEffect, useMemo, useState } from "react";
import * as assigneeDetailsCss from "./assignee-details.module.css";

const searcher = new Fuse<User>([], {
  keys: ["name", "email"],
  threshold: 0.3,
});

function useAssigneeList(assignee: User | null | undefined) {
  const team = useRequiredContext(TeamContext);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  useEffect(() => {
    setAllUsers(team.users.map((user) => user.user as User));
  }, [team.users]);

  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();
  const [searchText, setSearchText] = useState<string>("");
  const filteredUsers = useSearch(searcher, allUsers, searchText);
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const assigneeListItems: RedoListItem<User | null>[] = useMemo(() => {
    const userItems: RedoListItem<User>[] = filteredUsers.map((user) => {
      const name = user._id === CHATBOT_USER_ID ? "Chatbot" : user.name;

      return {
        type: "text",
        id: user._id,
        text: name,
        value: user,
        leadingItem: {
          type: "avatar",
          alt: "User profile picture",
          imageUrl: getUserAvatarUrl({ email: user.email }),
          name,
          size: UserImageSize.TINY,
        },
      };
    });

    const unassigned: RedoListItem<null> = {
      type: "text",
      id: "unassigned",
      text: "None",
      value: null,
    };

    return [unassigned, ...userItems];
  }, [filteredUsers]);

  const selectedItem = assignee?._id || "unassigned";

  return {
    searchText,
    setSearchText,
    inputRef,
    setInputRef,
    focusedIndex,
    setFocusedIndex,
    assigneeListItems,
    selectedItem,
  };
}

export const AssigneePickerDropdown = memo(function AssigneePickerDropdown({
  assignee,
  onAssigneeChange,
}: {
  assignee: User | null | undefined;
  onAssigneeChange: (assignee: User | null) => void;
}) {
  const {
    searchText,
    setSearchText,
    inputRef,
    setInputRef,
    focusedIndex,
    setFocusedIndex,
    assigneeListItems,
    selectedItem,
  } = useAssigneeList(assignee);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const isChatbot = assignee?._id === CHATBOT_USER_ID;

  const closeDropdown = () => {
    setDropdownOpen(false);
    setSearchText("");
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (dropdownOpen) {
          closeDropdown();
        }
      }}
    >
      <div ref={setDropdownAnchor}>
        <div
          className={assigneeDetailsCss.assigneeBadge}
          onClick={() => setDropdownOpen((prev) => !prev)}
        >
          <RedoBadge
            avatar={
              !isChatbot
                ? {
                    alt: "Assignee profile picture",
                    imageUrl: getUserAvatarUrl({
                      email: assignee?.email || "",
                      userId: assignee?._id,
                    }),
                    name: assignee?.name,
                  }
                : undefined
            }
            color="gray"
            segmentLeading={
              isChatbot ? { type: "icon", Icon: ChatBotIcon } : undefined
            }
            size="sm"
            text={assignee?.name || "None"}
          />
        </div>
        <div>
          <Dropdown
            anchor={dropdownAnchor}
            fitToAnchor={false}
            open={dropdownOpen}
          >
            <Flex
              className={assigneeDetailsCss.dropdownContainer}
              dir="column"
              gap="sm"
            >
              <input
                autoFocus
                className={assigneeDetailsCss.input}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search for an option..."
                ref={setInputRef}
                value={searchText}
              />
              <Divider />
              <RedoList
                focusedIndex={focusedIndex}
                items={assigneeListItems}
                itemSelected={({ item }) => {
                  closeDropdown();
                  onAssigneeChange(item.value);
                }}
                refToListenTo={inputRef}
                selectedItems={selectedItem}
                setFocusedIndex={setFocusedIndex}
                size="sm"
              />
            </Flex>
          </Dropdown>
        </div>
      </div>
    </ClickAwayListener>
  );
});

export const AssigneePickerInline = memo(function AssigneePickerInline({
  assignee,
  onAssigneeChange,
}: {
  assignee: User | null | undefined;
  onAssigneeChange: (assignee: User | null) => void;
}) {
  const {
    searchText,
    setSearchText,
    inputRef,
    setInputRef,
    focusedIndex,
    setFocusedIndex,
    assigneeListItems,
    selectedItem,
  } = useAssigneeList(assignee);

  return (
    <Flex dir="column" gap="sm">
      <input
        autoFocus
        className={assigneeDetailsCss.input}
        onChange={(event) => setSearchText(event.target.value)}
        placeholder="Search for an option..."
        ref={setInputRef}
        value={searchText}
      />
      <Divider />
      <RedoList
        focusedIndex={focusedIndex}
        items={assigneeListItems}
        itemSelected={({ item }) => {
          onAssigneeChange(item.value);
        }}
        refToListenTo={inputRef}
        selectedItems={selectedItem}
        setFocusedIndex={setFocusedIndex}
        size="sm"
      />
    </Flex>
  );
});
